import { useEffect } from 'react';
import i18next from 'i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useUndoStore } from '@/store/undoStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useTutorialStore, selectExampleTourActive } from '@/store/tutorialStore.ts';
import { saveProject } from '@/store/actions.ts';
import { capabilities } from '@/capabilities.ts';
import { getAudioContext, playSilentKick } from '@/audio/audioProcessor.ts';
import { resetOrient } from '@/hooks/useQuickOrient.ts';
import { env } from '@/config/environment.ts';
// Pure derivation module (its only import is a TYPE from projectStore, erased at compile), so
// naming it here cannot make a cycle back into this hook.
import { fillPresentation } from '@/components/SceneBoard/boardFillLogic.ts';
// Same shape of import, same reason: a pure module with no store imports at all.
import { adjacentShotId } from '@/components/SceneBoard/boardShotShortcuts.ts';

/**
 * Global keyboard shortcuts for timeline control.
 * Uses getState() inside the handler to avoid re-registering the listener
 * on every frame during playback (25+ selector deps → just [project]).
 */
export function useKeyboardShortcuts() {
  const project = useProjectStore(s => s.project);

  useEffect(() => {
    if (!project) return;

    const handler = (e: KeyboardEvent) => {
      const proj = useProjectStore.getState();
      const pb = usePlaybackStore.getState();
      const ui = useUiStore.getState();
      const undo = useUndoStore.getState();

      // Board-fill modal (owner item B, 2026-09-01): a blocking scrim that only stops pointers is not
      // blocking. Every shortcut here mutates or navigates the project behind it — including undo and
      // save — so nothing passes while the modal owns the screen. Store-derived rather than
      // `closest('[aria-modal]')`, because a scrim tap parks focus on <body>.
      if (fillPresentation(proj.boardFill, proj.boardFillModalDismissed) === 'modal') return;

      // Cmd+S / Ctrl+S — save project (disabled in review mode)
      if (e.code === 'KeyS' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (!proj.readOnly) saveProject();
        return;
      }

      // Cmd+Z / Ctrl+Z — undo (works even in inputs)
      if (e.code === 'KeyZ' && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        e.preventDefault();
        undo.undo();
        return;
      }

      // Cmd+Shift+Z / Ctrl+Shift+Z — redo (works even in inputs)
      if (e.code === 'KeyZ' && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        undo.redo();
        return;
      }

      // Cmd+V / Ctrl+V is deliberately NOT here. The WKWebView paste fallback moved to
      // src/clipboardPasteFallback.ts, installed once from main.tsx so it covers every route
      // rather than just the Timeline. It must not be re-added here: SceneBoardPage mounts
      // TimelineView on its Timeline tab, so two installers would double-paste on that tab.

      // Cmd+E / Ctrl+E — open export dialog on the NLE tab
      if (e.code === 'KeyE' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (!proj.readOnly) useUiStore.getState().openExportHub('nle');
        return;
      }

      // Cmd+Option+Right / Cmd+Option+Left — next / previous shot (20260923, owner). Option keeps it
      // off a text field's Cmd+Right / Cmd+Left, and the plain arrows' one-second seek further down
      // only runs with no Cmd/Ctrl held, so the two never meet. The board's twin lives in
      // useBoardShotShortcuts; the step itself (no wrap, first/last from no
      // selection) is the one shared adjacentShotId. The walk is the shots ON the lane:
      // getActiveScenes() — omitted scenes excluded, and only the picked scene when the Timeline is
      // scoped to one, because seek() clamps into that scene's window and a shot outside it would
      // leave the selection and the Viewer on different shots. A selection that is not on the lane
      // (a board pick from another scene) steps from the shot under the playhead instead, as does
      // no selection. Select, then seek to the shot's start: the same seek the arrows use, so it is
      // fine mid-playback. Above the input guard (and the Cmd/Ctrl bail-out below it) so the chord
      // is claimed on this whole route — but a focused text field, draw mode, and a running AI
      // generation all keep the selection where it is, like the arrows below. Draw mode matters
      // most: a key press fires no pointerdown, so the Viewer would close the drawing AFTER the
      // seek and offer the doodle against the new shot.
      if ((e.metaKey || e.ctrlKey) && e.altKey && !e.shiftKey
        && (e.code === 'ArrowRight' || e.code === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
        if (ui.drawingMode || ui.scriptAnnotationMode || useAiStore.getState().isAnyGenerating()) return;
        // The Getting started tour owns the selection while it runs (the board's twin has the same guard).
        if (selectExampleTourActive(useTutorialStore.getState())) return;
        const forward = e.code === 'ArrowRight' || (e.code !== 'ArrowLeft' && e.key === 'ArrowRight');
        const ids = proj.getActiveScenes().flatMap((sc) => sc.shots).map((s) => s.id);
        const sel = proj.selectedShotId;
        const current = sel != null && ids.includes(sel) ? sel : proj.getShotAtTime(pb.currentTime)?.id ?? null;
        const target = adjacentShotId(ids, current, forward);
        if (target != null) {
          proj.selectShot(target, true);
          pb.seek(proj.getShotStartTime(target));
        }
        return;
      }

      // Don't capture if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      // Don't intercept Cmd/Ctrl+letter (copy, paste, etc.)
      if (e.metaKey || e.ctrlKey) return;

      // Quick AI shortcuts (Alt/Option + key)
      if (e.altKey && !proj.readOnly && capabilities.aiGeneration) {
        if (e.code === 'KeyI') {
          e.preventDefault();
          if (ui.aiWorkspaceOpen && ui.aiWorkspaceTask === 'enhance') {
            ui.closeAiWorkspace();
          } else {
            // Landing tab is 'create' since 2026-08-25; Alt+I is the Enhance shortcut, so it
            // names its tab. Written BEFORE the open (T1+T2, 2026-09-01): the flow's open effect
            // now reads aiWorkspaceTab to decide whether to seed the playhead shot, so the tab
            // must already be committed when the workspace mounts — don't rely on React batching.
            ui.setAiWorkspaceTab('stylegrade');
            ui.openAiWorkspace('enhance');
          }
          return;
        }
        if (e.code === 'KeyV') {
          e.preventDefault();
          if (ui.aiWorkspaceOpen && ui.aiWorkspaceTask === 'video') {
            ui.closeAiWorkspace();
          } else {
            // Same as Alt+I: the video TASK lives on the Enhance tab, not the new Create landing,
            // and the tab is named BEFORE the open so the flow's tab-gated seed sees it.
            ui.setAiWorkspaceTab('stylegrade');
            ui.openAiWorkspace('video');
          }
          return;
        }
        if (e.code === 'KeyS') {
          e.preventDefault();
          ui.setScratchScope('custom');
          ui.openAudioWorkspace('tts');
          return;
        }
      }

      // Alt+A — toggle audio workspace
      if (e.altKey && !proj.readOnly && e.code === 'KeyA') {
        e.preventDefault();
        if (ui.audioWorkspaceOpen) {
          ui.closeAudioWorkspace();
        } else {
          ui.openAudioWorkspace();
        }
        return;
      }

      // Escape — close AI workspace (if not generating)
      if (e.code === 'Escape' && ui.aiWorkspaceOpen) {
        const ai = useAiStore.getState();
        if (!ai.isAnyGenerating()) {
          e.preventDefault();
          ui.closeAiWorkspace();
          return;
        }
      }

      // Escape — close audio workspace (if not recording)
      if (e.code === 'Escape' && ui.audioWorkspaceOpen && !ui.isRecording) {
        e.preventDefault();
        ui.closeAudioWorkspace();
        return;
      }

      // ? — toggle keyboard shortcuts overlay
      if (e.key === '?') {
        e.preventDefault();
        ui.toggleShortcutsOverlay();
        return;
      }

      const aiGenerating = useAiStore.getState().isAnyGenerating();
      const isDrawing = ui.drawingMode || ui.scriptAnnotationMode;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          if (isDrawing) break; // Block playback toggle during drawing
          // Resume + silent kick in user gesture stack (Safari requirement)
          if (!pb.isPlaying) {
            try { const ctx = getAudioContext(); ctx.resume().catch(() => {}); playSilentKick(ctx); } catch { /* ok */ }
          }
          pb.togglePlayback();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (!aiGenerating && !isDrawing) pb.seek(pb.currentTime - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (!aiGenerating && !isDrawing) pb.seek(pb.currentTime + 1);
          break;
        case 'Home':
          e.preventDefault();
          if (!aiGenerating && !isDrawing) pb.seek(proj.getViewOffset());
          break;
        case 'End':
          e.preventDefault();
          if (!aiGenerating && !isDrawing) pb.seek(proj.getViewOffset() + proj.getViewDuration());
          break;
        case 'Equal': // + key
        case 'NumpadAdd':
          e.preventDefault();
          ui.setZoom(ui.pixelsPerSecond + 20);
          break;
        case 'Minus':
        case 'NumpadSubtract':
          e.preventDefault();
          ui.setZoom(ui.pixelsPerSecond - 20);
          break;
        case 'KeyC': {
          e.preventDefault();
          // Auto-select current shot so the panel has something to show
          if (proj.selectedShotId === null) {
            const shot = proj.getShotAtTime(pb.currentTime);
            if (shot) proj.selectShot(shot.id);
          }
          ui.toggleCommentPanel();
          break;
        }

        // R — toggle auto-pause at shot boundaries
        case 'KeyR': {
          e.preventDefault();
          ui.setReviewAutoPlay(!ui.reviewAutoPlay);
          break;
        }

        case 'Delete':
        case 'Backspace': {
          if (proj.readOnly) break;
          if (proj.selectedAudioClipId !== null) {
            e.preventDefault();
            if (window.confirm(i18next.t('errors:deleteAudioClipConfirm'))) {
              useAudioStore.getState().removeAudioClip(proj.selectedAudioClipId);
            }
          }
          break;
        }

        // J/K/L shuttle controls
        case 'KeyJ': {
          e.preventDefault();
          if (!pb.isPlaying) {
            pb.setShuttleSpeed(-1);
            pb.play();
          } else if (pb.shuttleSpeed > 0) {
            // Forward → halve toward -1
            const next = pb.shuttleSpeed <= 1 ? -1 : pb.shuttleSpeed / 2;
            pb.setShuttleSpeed(next <= 1 && next > 0 ? -1 : next);
          } else {
            // Already reverse → double (max -8)
            pb.setShuttleSpeed(Math.max(-8, pb.shuttleSpeed * 2));
          }
          break;
        }
        case 'KeyK': {
          e.preventDefault();
          pb.pause();
          break;
        }
        case 'KeyL': {
          e.preventDefault();
          if (!pb.isPlaying) {
            pb.setShuttleSpeed(1);
            pb.play();
          } else if (pb.shuttleSpeed < 0) {
            // Reverse → halve toward 1
            const next = pb.shuttleSpeed >= -1 ? 1 : pb.shuttleSpeed / 2;
            pb.setShuttleSpeed(next >= -1 && next < 0 ? 1 : next);
          } else {
            // Already forward → double (max 8)
            pb.setShuttleSpeed(Math.min(8, pb.shuttleSpeed * 2));
          }
          break;
        }

        // D — toggle draw mode
        case 'KeyD': {
          e.preventDefault();
          ui.toggleDrawingMode();
          break;
        }

        // H — toggle generation history panel
        case 'KeyH': {
          e.preventDefault();
          ui.toggleHistoryPanel();
          break;
        }

        // M — toggle force-mobile layout (testing). Dev builds only (QA 2026-08-13, issue 6):
        // reachable by any production user, no visual feedback, and session-sticky — one
        // accidental keypress on the Timeline tab pinned the compact transport layout for the
        // whole session at any width.
        case 'KeyM': {
          if (!env.isDev) break;
          e.preventDefault();
          ui.toggleForceMobile();
          document.documentElement.classList.toggle('force-mobile');
          break;
        }

        // F — toggle fullscreen
        case 'KeyF': {
          e.preventDefault();
          useUiStore.getState().toggleFullscreen();
          break;
        }

        // G — toggle Welcome to AI dialog
        case 'KeyG': {
          e.preventDefault();
          if (ui.glowUpDialogOpen) {
            ui.closeGlowUpDialog();
          } else {
            ui.openGlowUpDialog();
          }
          break;
        }

        // T — reset and replay orient coach marks. One key, whichever walk-through belongs to the
        // tab QA are looking at: the Script tab has its own five marks (ScriptOrient.tsx).
        case 'KeyT': {
          e.preventDefault();
          resetOrient();
          if (ui.workspaceRoute === 'screenplay') ui.setScriptOrientStep(0);
          else ui.setOrientStep(0);
          break;
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [project]);
}
